import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components'
import { breakpoint } from '../layout/styles';
import { Button } from '../layout/Button'
import { Wysiwyg } from '../styles/Wysiwyg'
import { Container } from '../styles/Container'
import Image from "next/image";
import Link from "next/link";
import ReactHtmlParser from 'react-html-parser'

interface FlipGridProps {
  fields: {
    items: any,
    starting_image_position: string,
  },
  className?: string,
  variant?: any,
}

/**
 * In addition to being a shared component, this is also invoked
 * on the product detail page. @see ProductDetailPage.
 */
const FlipGrid = ({ fields, variant, ...props }: FlipGridProps) => {
  const { items, starting_image_position } = fields;
  const [descriptions, setDescriptions] = useState<string[]>([]);

  useEffect(() => {
    const newDescriptions = items.map((item: any) => item.description || '');
    setDescriptions(newDescriptions);
  }, [items]);

  return (
    <Section className={props?.className} variant={variant}>
      <StyleContainer variant={variant}>
        {items.map((item: any, index: number) => (
          <Item key={`flip-grid-item-${item?._metadata?.uid}`} image_position={starting_image_position}>
            <ImageContainer>
            {item.image && (item.image.url?.toLowerCase().includes('.gif')
                ? <img src={item.image.url} alt={item.image?.description} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <ItemImage
                    src={`${item.image?.url}?width=900&auto=webp&format=pjpg&quality=75`}
                    alt={item.image?.description}
                    layout="fill"
                  />
              )}
            </ImageContainer>
            <ItemContent>
              {variant !== 'product' && <h2>{item.title}</h2>}
              {variant === 'product' && <h3>{item.title}</h3>}
              <p dangerouslySetInnerHTML={{ __html: descriptions[index] }} />
              {item?.cta?.href && item?.cta?.title &&
                <Link href={item.cta.href} passHref>
                  <Button color={variant === 'product' ? 'red' : undefined}>{item.cta.title}</Button>
                </Link>
              }
            </ItemContent>
          </Item>
        )
        )}
      </StyleContainer>
    </Section>
  )
}

const Section = styled.section< { variant: any }>`
  position: relative;
  background-color: var(--lighter-blue);
  @media ${breakpoint.mobile} {
    padding: 0;
  }
  @media ${breakpoint.tablet} {
    padding: 0 0 7.875rem;
  }
  @media ${breakpoint.desktop} {
    padding: 5rem 0;
  }
  ${(props) => props.variant === 'product' && css`
    background-color: var(--white);
    && {
      padding: 0;
    }
  `}
`

const Telly = styled.span`
  position: absolute;
  top: -6.75rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  display: block;
  height: 8.5rem;
  width: 15.375rem;
  @media ${breakpoint.tablet} {
    display: none;
  }
`

const StyleContainer = styled(Container)`
  margin: 0 auto;
  ${(props) => props.variant === 'product' && css`
    padding: 0;
  `}
`

const Item = styled.article<{ image_position: string }>`
  background-color: var(--white);
  overflow: hidden;
  
  @media ${breakpoint.mobile} {
    display: flex;
    flex-direction: column-reverse;
  }
  
  @media ${breakpoint.tablet} {
    display: flex;
    flex-direction: row;
  }
  
  ${({image_position}) => image_position == 'left' && css`
    &:nth-child(even) {
      @media ${breakpoint.tablet} {
        flex-direction: row-reverse;
      }
      
      ${ItemContent} {
        padding: .75rem 3rem 0 3rem;
      }
    }
    &:nth-child(odd) {
      @media ${breakpoint.mobile} {
        margin-bottom: 3rem;
      }
    }
  `}
  ${({image_position}) => image_position == 'right' && css`
    &:nth-child(odd) {
      @media ${breakpoint.mobile} {
        ${ItemContent} {
          padding-left: 3rem;
          margin-bottom: 3rem;
        }
      }
      @media ${breakpoint.tablet} {
        flex-direction: row-reverse;
      }
    }
    &:nth-child(even) {
      ${ItemContent} {
        padding: 12px 0 0 48px;
        @media ${breakpoint.mobile} {
          padding-right: 48px;
          margin-bottom: 48px;
        }
      }
    }
  `}

  &:not(:last-child) {
    margin-bottom: 3rem;
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 50vw;
  max-height: 20rem;
  overflow: hidden;
  @media ${breakpoint.tablet_down} {
    margin-top: 3rem;
  }
  @media ${breakpoint.tablet} {
    flex: 0 0 50%;
    max-height: none;
    height: auto;
  }
`

const ItemImage = styled(Image)`
  object-fit: cover;
`

const ItemContent = styled(Wysiwyg)`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: flex-start;
  gap: 1.25rem;
  padding: .75rem 3rem 0 3rem;
  
  @media ${breakpoint.tablet} {
    flex: 0 0 50%;
    min-height: 19.5rem;
  }
  
  h2 {
    color: var(--black);
  }
  
  p {
    line-height: 1.5rem;
    
    sup {
      font-size: .7rem;
    }
  }

  ${(props) => props.variant === 'product' && css`
    h3 {
      color: var(--black);
    }
  `}
`

export default FlipGrid
